/**
 * Fundbox Fuse — vendor client (script load + FbxFuse init).
 *
 * Single module for the imperative integration:
 * - Idempotently load fuse.js and chain window.onFuseAPIReady
 * - new FbxFuse(partnerId, { container, events }) → window.FbxFuseClient
 * - launchFundboxFuseEmbed orchestrates load + init; caller invokes client.open()
 *
 */

import type { FbxFuseClient, FbxFuseStateEvent } from '@/enterprise/features/fundbox/fuse/fundboxFuse.types';

const FUNDBOX_FUSE_SCRIPT_ID = 'fuse-script';

const FBX_FUSE_READY_TIMEOUT_MS = 15_000;

interface LoadFundboxFuseScriptOptions {
  onFuseAPIReady?: () => void;
}

interface InitFundboxFuseClientParams {
  partnerId: string;
  container: HTMLElement;
  onStateReady: (event: FbxFuseStateEvent) => void;
  onStateChange: (event: FbxFuseStateEvent) => void;
}

interface LaunchFundboxFuseEmbedParams {
  scriptUrl: string;
  partnerId: string;
  container: HTMLElement;
  onStateReady: (event: FbxFuseStateEvent) => void;
  onStateChange: (event: FbxFuseStateEvent) => void;
}

let fuseScriptLoadPromise: Promise<void> | null = null;

function createFundboxFuseScript(scriptUrl: string): HTMLScriptElement {
  const script = document.createElement('script');
  script.id = FUNDBOX_FUSE_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.src = scriptUrl;
  return script;
}

function runFuseReadyHandlers(options?: LoadFundboxFuseScriptOptions): void {
  options?.onFuseAPIReady?.();
}

function waitForFbxFuse(
  options?: LoadFundboxFuseScriptOptions,
  timeoutMs: number = FBX_FUSE_READY_TIMEOUT_MS
): Promise<void> {
  if (window.FbxFuse) {
    runFuseReadyHandlers(options);
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error('Timed out waiting for FbxFuse after fuse.js loaded'));
    }, timeoutMs);

    const previousOnReady = window.onFuseAPIReady;

    window.onFuseAPIReady = (): void => {
      previousOnReady?.();
      window.clearTimeout(timeoutId);
      runFuseReadyHandlers(options);
      resolve();
    };
  });
}

function loadFundboxFuseScript(scriptUrl: string, options?: LoadFundboxFuseScriptOptions): Promise<void> {
  if (document.getElementById(FUNDBOX_FUSE_SCRIPT_ID)) {
    return fuseScriptLoadPromise ?? waitForFbxFuse(options);
  }

  fuseScriptLoadPromise = new Promise<void>((resolve, reject) => {
    const script = createFundboxFuseScript(scriptUrl);
    script.onerror = (): void => {
      fuseScriptLoadPromise = null;
      reject(new Error(`Fundbox fuse.js failed to load from ${scriptUrl}`));
    };

    document.head.appendChild(script);

    void waitForFbxFuse(options)
      .then(resolve)
      .catch((error: Error) => {
        fuseScriptLoadPromise = null;
        reject(error);
      });
  });

  return fuseScriptLoadPromise;
}

function initFundboxFuseClient(params: InitFundboxFuseClientParams): FbxFuseClient {
  if (!window.FbxFuse) {
    throw new Error('FbxFuse is not available — fuse.js may not have loaded');
  }

  window.FbxFuseClient = new window.FbxFuse(params.partnerId, {
    container: params.container,
    events: {
      onStateReady: params.onStateReady,
      onStateChange: params.onStateChange,
    },
  });

  return window.FbxFuseClient;
}

/** Clears window.FbxFuseClient between embed opens. */
export function resetFundboxFuseClient(): void {
  delete window.FbxFuseClient;
}

/** Clears the in-memory script load promise (for unit tests). */
export function resetFundboxFuseScriptLoaderForTests(): void {
  fuseScriptLoadPromise = null;
}

/** Loads fuse.js if needed, inits FbxFuse with partnerId + container, returns client for open(). */
export async function launchFundboxFuseEmbed(params: LaunchFundboxFuseEmbedParams): Promise<FbxFuseClient> {
  resetFundboxFuseClient();

  let client: FbxFuseClient | undefined;

  const initClient = (): FbxFuseClient => {
    client = initFundboxFuseClient({
      partnerId: params.partnerId,
      container: params.container,
      onStateReady: params.onStateReady,
      onStateChange: params.onStateChange,
    });
    return client;
  };

  if (window.FbxFuse) {
    return initClient();
  }

  await loadFundboxFuseScript(params.scriptUrl, {
    onFuseAPIReady: initClient,
  });

  if (!client) {
    throw new Error('FbxFuseClient was not initialized after fuse.js loaded');
  }

  return client;
}
